import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles, ArrowRight, Check } from "lucide-react";

// Phase 25.15a — DARA Concierge chatbot widget.
// Floating bottom-right pill that expands to a chat panel. Mounts on both
// public and authenticated routes (route-aware persona resolved server-side).

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Routes where we DO NOT want to mount the widget (e.g. login, embedded
// preview pages, certain admin tools). Edit freely.
const HIDDEN_PATHS = [
  "/login",
  "/signup",
  "/regulator-pack-preview", // it's a print-friendly artefact, no widget
  "/app/admin",              // already drowning in tooling
];

const TONE_LABEL_FOR_PATH = (path) => {
  const p = (path || "").toLowerCase();
  if (p.includes("/industries/") && ["mining", "water", "shipping", "energy", "industrial"].some(k => p.includes(k))) {
    return { kind: "industrial", label: "Industrial Concierge", greeting: "Tier-2 operator? Ask me about pilots, integrations, or the regulator pack." };
  }
  if (p.includes("/industrial-demo") || p.includes("/industrial/connector") || p.includes("/regulator-pack")) {
    return { kind: "industrial", label: "Industrial Concierge", greeting: "Walking through the demo? Happy to answer anything." };
  }
  if (p.includes("/pos-live") || p.startsWith("/app/pos") || p.includes("/barber") || p.includes("/smb")) {
    return { kind: "smb", label: "DARA Helper", greeting: "Hey — questions about how DARA helps your shop? Ask away." };
  }
  return { kind: "default", label: "DARA Concierge", greeting: "Welcome to DARA. Ask me anything about the platform." };
};

const SUGGESTED_FOR_PERSONA = {
  industrial: [
    "How long is the pilot?",
    "What historian systems do you support?",
    "Walk me through the security model",
  ],
  smb: [
    "How much does it cost?",
    "Will it connect to my Square?",
    "What does the daily brief look like?",
  ],
  default: [
    "How much does DARA cost?",
    "What does DARA actually do?",
    "Who is DARA for?",
  ],
};

function genSessionId() {
  try { return crypto.randomUUID(); } catch {
    return "s_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
}

export default function ChatbotWidget() {
  const location = useLocation();
  const path = location.pathname;
  const hidden = useMemo(() => HIDDEN_PATHS.some(h => path.startsWith(h)), [path]);

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [leadShown, setLeadShown] = useState(false);
  const [leadSent, setLeadSent] = useState(false);
  const [sessionId] = useState(() => {
    try {
      const k = "dara_chat_session";
      let id = localStorage.getItem(k);
      if (!id) { id = genSessionId(); localStorage.setItem(k, id); }
      return id;
    } catch {
      return genSessionId();
    }
  });
  const scrollRef = useRef(null);
  const tone = TONE_LABEL_FOR_PATH(path);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [open, messages.length]);

  // Show a welcome message once when first opened
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", text: tone.greeting, isGreeting: true }]);
    }
  }, [open, messages.length, tone.greeting]);

  if (hidden) return null;

  const token = (() => {
    try { return localStorage.getItem("dara_token"); } catch { return null; }
  })();

  const send = async (text) => {
    if (!text || busy) return;
    setInput("");
    setMessages(m => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const res = await fetch(`${API}/chatbot/message`, {
        method: "POST",
        headers,
        body: JSON.stringify({ session_id: sessionId, message: text, path }),
      });
      const data = await res.json();
      setMessages(m => [...m, {
        role: "assistant",
        text: data.assistant_message || "(no reply)",
        sources: data.sources || [],
      }]);
      if (data.auto_lead_captured) {
        setLeadSent(true);
      } else if (data.lead_capture_suggested && !leadShown && !leadSent) {
        setLeadShown(true);
      }
    } catch (e) {
      setMessages(m => [...m, {
        role: "assistant",
        text: "I'm having a brief connection hiccup. Try again in a moment, or drop your email below and the founder will reach out.",
      }]);
      setLeadShown(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chatbot-launcher"
          className="fixed bottom-5 right-5 z-50 group flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-4 py-3 hover:opacity-90 shadow-2xl"
          aria-label="Open DARA Concierge"
        >
          <MessageCircle className="w-4 h-4" />
          <span className="hidden sm:inline">Ask DARA</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-5 right-5 z-50 w-[calc(100vw-2.5rem)] max-w-[420px] h-[calc(100vh-5rem)] max-h-[640px] bg-black border border-zinc-800 flex flex-col shadow-2xl"
        >
          {/* Header */}
          <div className="border-b border-zinc-900 px-4 py-3 flex items-center justify-between bg-zinc-950/80">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-7 h-7 bg-[#FFB000] flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-3.5 h-3.5 text-black" />
              </div>
              <div className="min-w-0">
                <div className="font-heading font-bold text-sm text-white truncate" data-testid="chatbot-title">
                  {tone.label}
                </div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">
                  Powered by the DARA Engine
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="chatbot-close"
              className="text-zinc-500 hover:text-white p-1"
              aria-label="Close chatbot"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chatbot-messages">
            {messages.map((m, i) => (
              <Bubble key={i} role={m.role} text={m.text} isGreeting={m.isGreeting} />
            ))}
            {busy && (
              <div className="flex items-center gap-2 text-zinc-500 font-mono text-[10px] uppercase tracking-widest" data-testid="chatbot-typing">
                <span className="w-1.5 h-1.5 bg-zinc-500 rounded-full animate-pulse" />
                <span className="w-1.5 h-1.5 bg-zinc-500 rounded-full animate-pulse" style={{ animationDelay: "0.2s" }} />
                <span className="w-1.5 h-1.5 bg-zinc-500 rounded-full animate-pulse" style={{ animationDelay: "0.4s" }} />
                DARA Concierge is thinking…
              </div>
            )}
            {/* Lead capture inline form */}
            {leadShown && !leadSent && (
              <LeadForm
                sessionId={sessionId}
                path={path}
                onDone={() => { setLeadSent(true); setLeadShown(false); }}
              />
            )}
            {leadSent && (
              <div className="border border-[#00E676]/40 bg-[#00E676]/[0.05] px-3 py-2.5 flex items-center gap-2" data-testid="chatbot-lead-success">
                <Check className="w-4 h-4 text-[#00E676] flex-shrink-0" />
                <span className="text-sm text-[#00E676]">Got it — the founder will reach out shortly.</span>
              </div>
            )}
          </div>

          {/* Suggested chips (only when no user messages yet) */}
          {messages.filter(m => m.role === "user").length === 0 && (
            <div className="border-t border-zinc-900 px-3 py-2.5 flex flex-wrap gap-1.5" data-testid="chatbot-suggestions">
              {(SUGGESTED_FOR_PERSONA[tone.kind] || SUGGESTED_FOR_PERSONA.default).map((s, i) => (
                <button
                  key={i}
                  onClick={() => send(s)}
                  data-testid={`chatbot-suggestion-${i}`}
                  className="border border-zinc-800 hover:border-[#FFB000] text-zinc-300 hover:text-white font-mono text-[10px] uppercase tracking-widest px-2 py-1"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(input.trim()); }}
            className="border-t border-zinc-900 px-3 py-3 flex items-center gap-2 bg-zinc-950/40"
          >
            <input
              data-testid="chatbot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask anything about DARA…"
              disabled={busy}
              autoComplete="off"
              className="flex-1 bg-black border border-zinc-800 px-3 py-2 font-mono text-sm text-white placeholder:text-zinc-700 focus:outline-none focus:border-[#FFB000] disabled:opacity-50"
            />
            <button
              type="submit"
              data-testid="chatbot-send"
              disabled={busy || !input.trim()}
              className="bg-[#FFB000] text-black p-2 hover:opacity-90 disabled:opacity-50"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}

function Bubble({ role, text, isGreeting }) {
  if (role === "user") {
    return (
      <div className="flex justify-end" data-testid="chatbot-msg-user">
        <div className="bg-[#FFB000]/10 border border-[#FFB000]/40 text-white px-3 py-2 max-w-[85%] text-sm leading-relaxed">
          {text}
        </div>
      </div>
    );
  }
  return (
    <div className={`flex justify-start ${isGreeting ? "opacity-80" : ""}`} data-testid="chatbot-msg-assistant">
      <div className="bg-zinc-950/60 border border-zinc-900 text-zinc-100 px-3 py-2 max-w-[90%] text-sm leading-relaxed whitespace-pre-wrap">
        {text}
      </div>
    </div>
  );
}

function LeadForm({ sessionId, path, onDone }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Enter a valid email");
      return;
    }
    setBusy(true);
    try {
      const headers = { "Content-Type": "application/json" };
      try {
        const tok = localStorage.getItem("dara_token");
        if (tok) headers["Authorization"] = `Bearer ${tok}`;
      } catch { /* ignore */ }
      await fetch(`${API}/chatbot/lead`, {
        method: "POST",
        headers,
        body: JSON.stringify({ session_id: sessionId, email, name, path }),
      });
      onDone();
    } catch {
      setError("Couldn't save — try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-[#FFB000]/40 bg-[#FFB000]/[0.04] p-3 space-y-2" data-testid="chatbot-lead-form">
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] flex items-center gap-1.5">
        <ArrowRight className="w-3 h-3" /> Get a founder reply within 24 hours
      </div>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Your name (optional)"
        data-testid="chatbot-lead-name"
        className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-sm text-white placeholder:text-zinc-700 focus:outline-none focus:border-[#FFB000]"
      />
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email *"
        required
        data-testid="chatbot-lead-email"
        className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-sm text-white placeholder:text-zinc-700 focus:outline-none focus:border-[#FFB000]"
      />
      {error && <div className="font-mono text-[10px] text-[#FF6B47]">{error}</div>}
      <button
        type="submit"
        disabled={busy}
        data-testid="chatbot-lead-submit"
        className="w-full bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest py-2 hover:opacity-90 disabled:opacity-50"
      >
        {busy ? "Sending…" : "Send to founder"}
      </button>
    </form>
  );
}
